// Pestañas del módulo Administración (`.ag-tabs` / `.ag-tab`, handoff
// autogestión): Miembros · Invitaciones · Roles, Documentos · Calendario…
//
// Patrón WAI-ARIA de tabs con activación manual: las flechas
// izquierda/derecha (e Inicio/Fin) solo mueven el foco y Enter/Espacio
// —el click nativo del <button>— cambian de pestaña. Manual y no
// automática porque cada pestaña suele disparar su propia consulta y
// recorrerlas con el teclado no debe pedir datos que nadie va a leer.
// Tabulador entra a la pestaña activa (tabIndex roving) y sale del grupo.
//
// Con `idBase` las pestañas quedan enlazadas a sus paneles
// (aria-controls ↔ aria-labelledby); el consumidor pone
// `{...tabPanelProps(idBase, value)}` en el panel visible.

import { useRef } from "react";
import type { KeyboardEvent } from "react";

export type TabItem<T extends string> = {
  value: T;
  label: string;
  /** Contador mono opcional (p. ej. miembros, invitaciones). */
  count?: number;
  /** Frase del title; por defecto «Ver {label}» en minúscula inicial. */
  title?: string;
};

export type TabsProps<T extends string> = {
  value: T;
  tabs: readonly TabItem<T>[];
  onChange: (value: T) => void;
  /** Nombre accesible del tablist, p. ej. «Secciones de equipo». */
  label: string;
  /** Prefijo de ids para enlazar pestañas y paneles. */
  idBase?: string;
  className?: string;
};

// «Miembros» → «Ver miembros», sin tocar siglas («IA», «RUT»).
function defaultTitle(label: string): string {
  const [first = "", second = ""] = label;
  const lead = second && second === second.toLowerCase() ? first.toLowerCase() : first;
  return `Ver ${lead}${label.slice(1)}`;
}

export function tabId(idBase: string, value: string): string {
  return `${idBase}-tab-${value}`;
}

export function tabPanelId(idBase: string, value: string): string {
  return `${idBase}-panel-${value}`;
}

/** Props del panel que corresponde a la pestaña `value`. */
export function tabPanelProps(idBase: string, value: string) {
  return {
    id: tabPanelId(idBase, value),
    role: "tabpanel" as const,
    "aria-labelledby": tabId(idBase, value),
    tabIndex: 0,
  };
}

export function Tabs<T extends string>({
  value,
  tabs,
  onChange,
  label,
  idBase,
  className,
}: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  // Si value no coincide con ninguna pestaña (estado de la URL cargando,
  // pestaña retirada por capacidad), la primera recibe el foco del
  // Tabulador para que el grupo siga siendo alcanzable — igual que Seg.
  const tabbableIdx = Math.max(
    0,
    tabs.findIndex((t) => t.value === value)
  );

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []
    );
    const current = buttons.findIndex((b) => b === document.activeElement);
    if (current === -1 || buttons.length === 0) return;
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (current + 1) % buttons.length;
    else if (e.key === "ArrowLeft") next = (current - 1 + buttons.length) % buttons.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = buttons.length - 1;
    if (next === null) return;
    e.preventDefault();
    buttons[next]?.focus();
  }

  return (
    <div
      ref={listRef}
      className={className ? `ag-tabs ${className}` : "ag-tabs"}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((t, i) => {
        const selected = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            id={idBase ? tabId(idBase, t.value) : undefined}
            aria-controls={idBase ? tabPanelId(idBase, t.value) : undefined}
            aria-selected={selected}
            tabIndex={i === tabbableIdx ? 0 : -1}
            title={t.title ?? defaultTitle(t.label)}
            className={selected ? "ag-tab ag-tab--on" : "ag-tab"}
            onClick={() => onChange(t.value)}
          >
            {t.label}
            {t.count != null && <span className="ag-tab__n">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
